import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Minus, Plus, ShoppingBag, X } from "lucide-react";
import { toast } from "sonner";
import { useStore } from "@/context/StoreContext";
import { inr } from "@/lib/api";
import { Stars } from "@/components/ProductCard";

export default function QuickView({ product, onClose }) {
  const { addToCart } = useStore();
  const navigate = useNavigate();
  const [variant, setVariant] = useState(null);
  const [qty, setQty] = useState(1);

  useEffect(() => {
    if (product) {
      setVariant(product.variants[0].label);
      setQty(1);
    }
  }, [product]);

  const price = product?.variants.find((v) => v.label === variant)?.price || product?.price;

  return (
    <AnimatePresence>
      {product && (
        <>
          <motion.div
            className="fixed inset-0 z-[160] bg-black/70 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            data-testid="quickview-overlay"
          />
          <motion.div
            data-testid="quickview-modal"
            className="fixed left-1/2 top-1/2 z-[170] w-[92vw] max-w-2xl -translate-x-1/2 -translate-y-1/2 overflow-hidden rounded-3xl glass"
            style={{ background: "var(--mn-bg2)" }}
            initial={{ opacity: 0, scale: 0.92, y: 24 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.94, y: 12 }}
            transition={{ type: "spring", stiffness: 260, damping: 24 }}
          >
            <button
              data-testid="quickview-close"
              onClick={onClose}
              aria-label="Close quick view"
              className="btn-outline-glow absolute right-4 top-4 z-10 flex h-9 w-9 items-center justify-center rounded-full"
            >
              <X size={15} />
            </button>
            <div className="grid grid-cols-1 sm:grid-cols-2">
              <div className="relative aspect-square sm:aspect-auto">
                <img src={product.images[0]} alt={product.name} className="h-full w-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent sm:bg-gradient-to-r" />
              </div>
              <div className="flex flex-col p-6 sm:p-8">
                <p className="font-mono text-[0.6rem] uppercase tracking-[0.25em]" style={{ color: "var(--mn-gold)" }}>
                  {product.type} · Mother Nature
                </p>
                <h3 className="mt-2 font-serif text-xl sm:text-2xl">{product.name}</h3>
                <p className="mt-1 text-sm italic" style={{ color: "var(--mn-text2)" }}>{product.tagline}</p>
                <div className="mt-2 flex items-center gap-2">
                  <Stars value={product.rating} />
                  <span className="font-mono text-xs" style={{ color: "var(--mn-text2)" }}>{product.rating}</span>
                </div>
                <div className="mt-4 flex flex-wrap gap-2">
                  {product.variants.map((v) => (
                    <button
                      key={v.label}
                      data-testid={`quickview-variant-${v.label}`}
                      onClick={() => setVariant(v.label)}
                      className={`rounded-full border px-4 py-1.5 font-mono text-xs transition-all ${
                        variant === v.label ? "glow-gold" : ""
                      }`}
                      style={{
                        borderColor: variant === v.label ? "var(--mn-gold)" : "var(--mn-glass-border)",
                        color: variant === v.label ? "var(--mn-gold)" : "var(--mn-text2)",
                      }}
                    >
                      {v.label}
                    </button>
                  ))}
                </div>
                <div className="mt-4 flex items-center justify-between">
                  <div className="flex items-center gap-3 rounded-full border px-3 py-2" style={{ borderColor: "var(--mn-glass-border)" }}>
                    <button data-testid="quickview-qty-minus" onClick={() => setQty((q) => Math.max(1, q - 1))} aria-label="Decrease"><Minus size={13} /></button>
                    <span className="w-4 text-center font-mono text-sm" data-testid="quickview-qty">{qty}</span>
                    <button data-testid="quickview-qty-plus" onClick={() => setQty((q) => Math.min(10, q + 1))} aria-label="Increase"><Plus size={13} /></button>
                  </div>
                  <span className="font-mono text-xl" style={{ color: "var(--mn-gold)" }} data-testid="quickview-price">{inr(price * qty)}</span>
                </div>
                <div className="mt-5 flex gap-2">
                  <button
                    data-testid="quickview-add-to-cart"
                    onClick={() => {
                      addToCart(product, variant, qty);
                      toast.success(`${product.name} added to your bag`);
                      onClose();
                    }}
                    className="btn-gold flex flex-1 items-center justify-center gap-2 rounded-full py-3 text-sm"
                  >
                    <ShoppingBag size={15} /> Add to Bag
                  </button>
                  <button
                    data-testid="quickview-full-details"
                    onClick={() => { onClose(); navigate(`/product/${product.slug}`); }}
                    className="btn-outline-glow rounded-full px-5 text-sm"
                  >
                    Details
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
